"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";

// v7 — render to document.body via portal. Was being rendered as a child of
// <Sidebar/>, which has `md:translate-x-0` on desktop for the mobile slide
// behavior. Any CSS transform on an ancestor turns that ancestor into the
// containing block for descendant `position: fixed` elements (CSS spec, not
// a bug) — so the modal was "fixed" inside the 244px sidebar box, not the
// viewport. Portaling to body sidesteps the entire ancestor chain.
export function Modal({
  open,
  onClose,
  children,
  className,
  width = 560,
  ariaLabel = "Dialog",
  ariaLabelledBy,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  width?: number;
  ariaLabel?: string;
  ariaLabelledBy?: string;
}) {
  const [mounted, setMounted] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return;
    const previousFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const root = rootRef.current;
    const siblings = Array.from(document.body.children).filter(
      (element): element is HTMLElement =>
        element instanceof HTMLElement && element !== root
    );
    const siblingState = siblings.map((element) => ({
      element,
      inert: element.inert,
      ariaHidden: element.getAttribute("aria-hidden"),
    }));
    for (const sibling of siblings) {
      sibling.inert = true;
      sibling.setAttribute("aria-hidden", "true");
    }

    const focusableSelector = [
      'button:not([disabled]):not([tabindex="-1"])',
      'a[href]:not([tabindex="-1"])',
      'input:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      '[contenteditable="true"]:not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(",");
    const focusFrame = requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      const initial =
        dialog?.querySelector<HTMLElement>("[autofocus]") ??
        dialog?.querySelector<HTMLElement>(focusableSelector) ??
        dialog;
      initial?.focus({ preventScroll: true });
    });

    const onKey = (event: KeyboardEvent) => {
      const roots = document.querySelectorAll<HTMLElement>(
        "[data-pauta-dialog-root]"
      );
      if (roots.item(roots.length - 1) !== root) return;
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(focusableSelector)
      ).filter(
        (element) =>
          !element.hidden &&
          !element.closest("[inert]") &&
          element.getAttribute("aria-disabled") !== "true" &&
          element.getAttribute("aria-hidden") !== "true" &&
          element.getClientRects().length > 0
      );
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!dialog.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKey);
      for (const state of siblingState) {
        state.element.inert = state.inert;
        if (state.ariaHidden === null) {
          state.element.removeAttribute("aria-hidden");
        } else {
          state.element.setAttribute("aria-hidden", state.ariaHidden);
        }
      }
      if (previousFocus?.isConnected) {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, [open]);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          ref={rootRef}
          key="pauta-modal-root"
          data-pauta-dialog-root
          className="fixed inset-0 z-[60] flex items-start justify-center pt-[8vh] md:pt-[14vh] px-3 md:px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabelledBy ? undefined : ariaLabel}
            aria-labelledby={ariaLabelledBy}
            tabIndex={-1}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            style={{
              width: `min(${width}px, calc(100vw - 24px))`,
              flexShrink: 0,
            }}
            // max-h accounts for the container's pt-[8vh]/md:pt-[14vh] top
            // offset so the modal never runs off the bottom of the viewport
            // (8vh+88vh and 14vh+82vh both leave ~4vh breathing room). `flex
            // flex-col` lets a child scroll region claim the remaining height.
            className={cn(
              "focus-ring relative z-10 flex flex-col max-h-[88vh] md:max-h-[82vh] rounded-xl border border-[var(--border-strong)] bg-[var(--surface-elevated)] shadow-lg overflow-hidden",
              className
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
